import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MessageCircle, Clock, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiClient, formatApiError } from "@/lib/api";
import { CONTACT, WHATSAPP_URL, ASSIGNMENT_TYPES, FALLBACK_DEADLINES } from "./data";
import { SectionHeading } from "./Services";

const inputCls = "border-[#D4AF37]/25 bg-white text-[#12294D]";
const selectCls = "border-[#D4AF37]/25 bg-white text-[#12294D]";
const contentCls = "border-[#D4AF37]/25 bg-[#F3EEE2] text-[#12294D]";

const initialForm = {
  name: "", email: "", whatsapp: "", university: "", course: "",
  assignment_type: ASSIGNMENT_TYPES[1], word_count: 1000, deadline: FALLBACK_DEADLINES[2].label,
};

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    apiClient.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const email = settings?.email || CONTACT.email;
  const phone = settings?.phone || CONTACT.phone;
  const waRaw = (settings?.whatsapp || CONTACT.whatsapp).replace(/[^0-9]/g, "");
  const waUrl = `https://wa.me/${waRaw}`;

  const channels = [
    { icon: Mail, label: "Email Us", value: email, href: `mailto:${email}`, testid: "contact-email-link" },
    { icon: Phone, label: "Call Us (UK)", value: phone, href: `tel:${phone.replace(/\s/g, "")}`, testid: "contact-phone-link" },
    { icon: MessageCircle, label: "WhatsApp", value: settings?.whatsapp || CONTACT.whatsapp, href: waUrl, testid: "contact-whatsapp-link" },
    { icon: Clock, label: "Response Time", value: "Under 15 minutes · 24/7", href: null, testid: "contact-hours" },
  ];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/enquiries", { ...form, word_count: Number(form.word_count) });
      toast.success("Quote request sent! We'll reply within 15 minutes.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiError(err, "Could not send your request. Please try WhatsApp instead."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36" data-testid="quote-section">
      <SectionHeading id="quote" overline="Get a Free Quote" title="Sixty seconds to" accent="your exact price." />
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
          className="space-y-4"
        >
          {channels.map((c) => {
            const inner = (
              <>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5">
                  <c.icon size={18} className="text-[#A07E1F]" />
                </div>
                <div>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#5E6E85]">{c.label}</p>
                  <p className="mt-1 break-all text-base text-[#12294D]">{c.value}</p>
                </div>
              </>
            );
            return c.href ? (
              <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={c.testid} className="luxury-card flex items-center gap-5 rounded-xl p-5 transition-transform duration-300 hover:-translate-y-0.5">
                {inner}
              </a>
            ) : (
              <div key={c.label} data-testid={c.testid} className="luxury-card flex items-center gap-5 rounded-xl p-5">
                {inner}
              </div>
            );
          })}
          <a
            href={`${waUrl}?text=${encodeURIComponent("Hi! I'd like a free quote for my assignment.")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="quote-whatsapp-cta"
            className="flex items-center justify-center gap-3 rounded-full border border-[#D4AF37]/50 py-4 font-mono-accent text-[11px] font-bold uppercase tracking-[0.18em] text-[#A07E1F] transition-colors duration-300 hover:bg-[#D4AF37]/10"
          >
            <MessageCircle size={16} /> Chat on WhatsApp — Fastest Reply
          </a>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.33, 1, 0.68, 1] }}
          className="luxury-card rounded-xl p-6 sm:p-10"
          data-testid="enquiry-form"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="q-university" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">University *</Label>
              <Input id="q-university" required value={form.university} onChange={(e) => set("university", e.target.value)} data-testid="quote-university-input" placeholder="Your university" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-course" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Course *</Label>
              <Input id="q-course" required value={form.course} onChange={(e) => set("course", e.target.value)} data-testid="quote-course-input" placeholder="e.g. BSc Nursing" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Assignment Type *</Label>
              <Select value={form.assignment_type} onValueChange={(v) => set("assignment_type", v)}>
                <SelectTrigger data-testid="quote-type-select" className={selectCls}><SelectValue /></SelectTrigger>
                <SelectContent className={contentCls}>
                  {ASSIGNMENT_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`quote-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-words" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Word Count *</Label>
              <Input id="q-words" type="number" min={100} step={50} required value={form.word_count} onChange={(e) => set("word_count", e.target.value)} data-testid="quote-words-input" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Deadline *</Label>
              <Select value={form.deadline} onValueChange={(v) => set("deadline", v)}>
                <SelectTrigger data-testid="quote-deadline-select" className={selectCls}><SelectValue /></SelectTrigger>
                <SelectContent className={contentCls}>
                  {FALLBACK_DEADLINES.map((d) => <SelectItem key={d.value} value={d.label} data-testid={`quote-deadline-${d.value}`}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-name" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Name *</Label>
              <Input id="q-name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="quote-name-input" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-whatsapp" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">WhatsApp Number *</Label>
              <Input id="q-whatsapp" required value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="quote-whatsapp-input" placeholder="+44 ..." className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-email" className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]">Email *</Label>
              <Input id="q-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="quote-email-input" placeholder="you@university.edu" className={inputCls} />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="quote-submit-button"
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] py-4 font-mono-accent text-xs font-bold uppercase tracking-[0.2em] text-[#0A1E3C] transition-colors duration-300 hover:bg-[#C9A227] disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {loading ? "Sending..." : "Get My Free Quote"}
          </button>
          <p className="mt-4 text-center text-xs text-[#5E6E85]">Free, no-obligation quote. Average reply time: under 15 minutes.</p>
        </motion.form>
      </div>
    </section>
  );
};
